/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse, Canceler
} from 'axios'
import ApiError from './ApiError';
import { AuthResponse } from './types';
import RefreshTokenError from './error/RefreshTokenError';
import { OktaStorage } from '../OktaStorage';
import fileDownload from 'js-file-download';
import { ErrorResponse } from '../../types/api';
import globalConfig from '../../config';

interface G17EcoConfig extends AxiosRequestConfig {
  baseURL: string;
}

interface G17EcoRequestConfig extends AxiosRequestConfig {
  skipAuthRetry?: boolean;
  retryCount?: number;
}

export class BaseApi {
  protected httpClient: AxiosInstance;
  private refreshRequest?: Promise<void>;

  /**
   * Max number of retry, request can be executed using refresh flow.
   */
  private maxRetryCount = 1;

  constructor(
    protected readonly config: G17EcoConfig,
    protected readonly tokenStorage: typeof OktaStorage,
  ) {
    const clientConfig: G17EcoRequestConfig = {
      headers: {
        'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0,private',
      },
      ...this.config,
      skipAuthRetry: !this.tokenStorage.supportRefreshToken(),
    }

    this.httpClient = axios.create(clientConfig);
    this.httpClient.interceptors.request.use((c) => {
      // Axios have forked their interface between AxiosRequestConfig and InternalAxiosRequestConfig
      // So for now, this has to use 'Internal' and handle headers differently...
      // Hopefully they improve in the future.
      c.headers.set('Authorization', this.tokenStorage.getAuthHeaderValue());
      if (globalConfig.release) {
        c.headers['X-Client-Version'] = globalConfig.release;
      }

      return c;
    });
    this.httpClient.interceptors.response.use(this.checkResponse, this.handleErrorResponse);
  }

  /**
   * Generic response interceptor based on G17Eco api response.
   *
   * Currently G17Eco api is not following proper REST best practices of using
   * HTTP status, for error handling, but instead it will return status code 200
   * and use response body "success" property to determine if request was successful
   *
   * Interceptor will convert such responses (data.success false) to errors
   * will return, that will be checked and converted to ApiError.
   */
  private checkResponse(response: AxiosResponse) {
    if (response.config.responseType !== 'blob' && response.data.success !== true) {
      throw new ApiError(response.data.userMessage)
    }

    return response;
  }


  private handleErrorResponse = async (error: AxiosError<ErrorResponse>) => {
    const status = error.response?.status;
    if (status === 401 && error.config && !this.shouldSkipRetry(error.config)) {
      return this.retryWithRefreshToken(error.config)
    }

    if (error.response?.data) {
      const { message, userMessage } = error.response.data;

      if (status === 403) {
        // Assume 403 is user safe message as fallback
        throw new ApiError(userMessage ?? message, status)
      }

      throw new ApiError(userMessage, status)
    }

    throw error;
  }

  /**
   * Request explicity marked that should skip auth retry or
   * request that have already reach max number of retry attempts.
   * Safe guard agains possible never ending refresh auth token flow with retry.
   */
  private shouldSkipRetry(config: G17EcoRequestConfig) {
    return config.skipAuthRetry || Number(config.retryCount) > this.maxRetryCount;
  }

  private async retryWithRefreshToken(config: G17EcoRequestConfig) {
    await this.refreshToken();

    // Update request config header with latest value
    this.setAuthHeader(config);
    config.retryCount = (config.retryCount ?? 0) + 1;

    return this.request(config);
  }

  private setAuthHeader(config: AxiosRequestConfig) {
    if (config.headers) {
      config.headers.Authorization = this.tokenStorage.getAuthHeaderValue();
    }
    return config;
  }

  private async refreshToken() {

    // Currently in progress
    if (this.refreshRequest) {
      return this.refreshRequest;
    }

    const refreshToken = this.tokenStorage.getRefreshToken();
    if (!refreshToken) {
      throw new RefreshTokenError(RefreshTokenError.NotAvailable);
    }

    this.refreshRequest = this.post<AuthResponse>(
      'auth/refresh',
      { refreshToken },
      { skipAuthRetry: true },
    ).then(result => {
      this.updateAuthDetails(result.data);
      this.refreshRequest = undefined;
    }).catch(error => {
      this.refreshRequest = undefined;
      if (error.response?.status === 401 || error.status === 401) {
        this.tokenStorage.clearAuthTokens();
        // Token is not valid
        throw new RefreshTokenError(RefreshTokenError.FailedToRefresh);
      }
      // Generic error, keep token
      throw error
    })

    return this.refreshRequest;
  }

  /**
   * After successfull authentication request, update config with latest token values
   * Storage will be updated as well, for persistent storage
   */
  private updateAuthDetails(data: AuthResponse) {
    this.tokenStorage.setAuthTokens(data.token, data.refreshToken);
  }

  /**
   * Reset config tokens and update default Authorization header
   */
  private async clearAuthDetails() {
    await this.tokenStorage.clearAuthTokens();
  }

  /**
   * Trigger logout call
   *
   * At the moment it only clears token storage and current config tokens.
   * No actual api call is done, as there are sessions in api.
   */
  public async logout() {
    return this.clearAuthDetails().then(() => true).catch(err => {
      console.error(err);
      return false
    });
  }

  protected downloadBlob(response: AxiosResponse<Blob>) {
    const headerLine = response.headers['content-disposition'] ?? '';
    const startFileNameIndex = headerLine.indexOf('"') + 1;
    const endFileNameIndex = headerLine.lastIndexOf('"');
    const filename = headerLine.substring(startFileNameIndex, endFileNameIndex) || 'download.csv';
    return fileDownload(response.data, filename);
  }

  public createCancelToken(executor: (cancel: Canceler) => void) {
    return new axios.CancelToken(executor);
  }

  /**
   * Generic request pass through to internal http client.
   */
  public async request<T = any>(config: G17EcoRequestConfig) {
    return this.httpClient.request<T>(config);
  }

  public async get<T = any>(url: string, config?: G17EcoRequestConfig) {
    return this.httpClient.get<T>(url, config);
  }

  public async post<T = any>(url: string, data?: any, config?: G17EcoRequestConfig) {
    return this.httpClient.post<T>(url, data, config);
  }

  public async patch<T = any>(url: string, data?: any, config?: G17EcoRequestConfig) {
    return this.httpClient.patch<T>(url, data, config);
  }

  public async put<T = any>(url: string, data?: any, config?: G17EcoRequestConfig) {
    return this.httpClient.put<T>(url, data, config);
  }

  public async delete<T = any>(url: string, config?: G17EcoRequestConfig) {
    return this.httpClient.delete<T>(url, config);
  }

}
